import { apiFetch } from "../lib/api";
import { HierarchyAccessError, id, object } from "./api";
import type { TranscriptSelection } from "./transcript";

// Persist only this request identity, never file bytes or signed storage URLs.
export interface PendingUpload {
  id: string;
  artifactId: string;
  versionId: string;
  name: string;
  contentType: string;
  kind: "document" | "image" | "file";
  size: number;
  sha256: string;
}
export interface UploadedReference { artifact_id: string; version_id: string }
export class UploadExpiredError extends Error {
  constructor() { super("This upload expired. Check its saved result before starting again."); }
}

export function pendingUpload(raw: unknown): PendingUpload {
  const value = object(raw);
  if (Object.keys(value).some((key) => !["id", "artifactId", "versionId", "name", "contentType", "kind", "size", "sha256"].includes(key)) ||
    typeof value.name !== "string" || !value.name.trim() || value.name.length > 512 || /[\x00-\x1f\x7f]/.test(value.name) ||
    typeof value.contentType !== "string" || !value.contentType || value.contentType.length > 256 || /[\r\n]/.test(value.contentType) ||
    !["document", "image", "file"].includes(String(value.kind)) || !Number.isSafeInteger(value.size) || Number(value.size) <= 0 || Number(value.size) > (100 << 20) ||
    typeof value.sha256 !== "string" || !/^[a-f0-9]{64}$/.test(value.sha256)) throw new HierarchyAccessError();
  const result = { id: id(value.id), artifactId: id(value.artifactId), versionId: id(value.versionId), name: value.name,
    contentType: value.contentType, kind: value.kind as PendingUpload["kind"], size: Number(value.size), sha256: value.sha256 };
  if (result.id.length > 200 || result.artifactId.length > 256 || result.versionId.length > 256 || (result.kind === "image" && (result.size > (20 << 20) || !["image/jpeg", "image/png", "image/webp"].includes(result.contentType.split(";")[0].trim().toLowerCase())))) throw new HierarchyAccessError();
  return result;
}

export async function prepareUpload(file: File, previous?: PendingUpload, signal?: AbortSignal): Promise<{ pending: PendingUpload; bytes: ArrayBuffer }> {
  if (file.size <= 0 || file.size > (100 << 20)) throw new Error("Choose a nonempty file up to 100 MB.");
  signal?.throwIfAborted();
  const bytes = await file.arrayBuffer();
  const sha256 = Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", bytes)), (byte) => byte.toString(16).padStart(2, "0")).join("");
  signal?.throwIfAborted();
  if (previous) {
    const pending = pendingUpload(previous);
    if (pending.size !== bytes.byteLength || pending.sha256 !== sha256) throw new Error("Choose the original file to resume this upload.");
    return { pending, bytes };
  }
  const types: Record<string, string> = { txt: "text/plain", md: "text/markdown", csv: "text/csv", json: "application/json", yaml: "application/yaml", yml: "application/yaml", pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp" };
  const contentType = file.type || types[file.name.split(".").at(-1)?.toLowerCase() ?? ""] || "application/octet-stream";
  const kind = contentType.startsWith("image/") ? "image" : contentType.startsWith("text/") || ["application/json", "application/yaml", "application/pdf"].includes(contentType) ? "document" : "file";
  const pending = pendingUpload({ id: crypto.randomUUID(), artifactId: crypto.randomUUID(), versionId: crypto.randomUUID(),
    name: file.name, contentType, kind, size: bytes.byteLength, sha256 });
  return { pending, bytes };
}

function receipt(raw: unknown, selection: TranscriptSelection, pending: PendingUpload): UploadedReference {
  const value = object(raw), artifact = object(value.artifact), version = object(value.version);
  if (artifact.id !== pending.artifactId || artifact.accountId !== selection.accountId || artifact.workspaceId !== selection.workspaceId ||
    (artifact.projectId || undefined) !== selection.projectId || artifact.headVersionId !== pending.versionId || artifact.kind !== pending.kind || artifact.title !== pending.name ||
    version.id !== pending.versionId || version.artifactId !== pending.artifactId || version.accountId !== selection.accountId || version.workspaceId !== selection.workspaceId ||
    typeof version.sha256 !== "string" || !/^[a-f0-9]{64}$/.test(version.sha256) ||
    version.contentType !== (pending.kind === "image" ? "image/png" : pending.contentType) ||
    (pending.kind !== "image" && version.sha256 !== pending.sha256)) throw new HierarchyAccessError();
  return { artifact_id: pending.artifactId, version_id: pending.versionId };
}

function root(selection: TranscriptSelection): string { return `/code/workspaces/${encodeURIComponent(selection.workspaceId)}/artifact-uploads`; }

export async function finishUpload(selection: TranscriptSelection, pending: PendingUpload, signal?: AbortSignal): Promise<UploadedReference | undefined> {
  const response = await apiFetch(`${root(selection)}/${encodeURIComponent(pending.id)}/complete`, { method: "POST", credentials: "include", cache: "no-store", signal });
  if (response.ok) return receipt(await response.json(), selection, pending);
  if (response.status === 410) {
    const error = object(await response.json());
    if (error.error === "upload_expired") throw new UploadExpiredError();
    throw new HierarchyAccessError();
  }
  await response.body?.cancel();
  // Completion may precede a lost begin/PUT response. These responses do not
  // grant a new identity; the caller can replay the same checksum-bound ticket.
  if ([400, 404, 422].includes(response.status)) return undefined;
  throw new HierarchyAccessError();
}

export async function transferUpload(selection: TranscriptSelection, pending: PendingUpload, bytes: ArrayBuffer,
  authorize: (signal: AbortSignal) => Promise<unknown>, signal?: AbortSignal): Promise<UploadedReference> {
  const deadline = AbortSignal.timeout(120_000);
  const bounded = signal ? AbortSignal.any([signal, deadline]) : deadline;
  await authorize(bounded);
  const recovered = await finishUpload(selection, pending, bounded);
  if (recovered) { await authorize(bounded); bounded.throwIfAborted(); return recovered; }
  await authorize(bounded);
  const response = await apiFetch(root(selection), { method: "POST", credentials: "include", cache: "no-store", signal: bounded,
    headers: { "content-type": "application/json" }, body: JSON.stringify({ id: pending.id, operation: "create",
      artifact: { id: pending.artifactId, title: pending.name, kind: pending.kind, projectId: selection.projectId },
      version: { id: pending.versionId, contentType: pending.contentType, originalName: pending.name }, sha256: pending.sha256, size: pending.size }) });
  if (!response.ok) { await response.body?.cancel(); throw new Error("The upload couldn't be started. Its saved identity was kept."); }
  const ticket = object(await response.json());
  if (ticket.id !== pending.id || ticket.method !== "PUT" || typeof ticket.url !== "string" || typeof ticket.expiresAt !== "string" || !(Date.parse(ticket.expiresAt) > Date.now())) throw new HierarchyAccessError();
  const url = new URL(ticket.url);
  if (url.protocol !== "https:" || url.username || url.password || url.hash) throw new HierarchyAccessError();
  const headers = new Headers();
  for (const [name, value] of Object.entries(object(ticket.headers))) {
    if (typeof value !== "string" || /[\r\n]/.test(value)) throw new HierarchyAccessError();
    const key = name.toLowerCase();
    if (key === "content-length") { if (value !== String(pending.size)) throw new HierarchyAccessError(); continue; }
    if (!["content-type", "x-amz-checksum-sha256", "x-amz-sdk-checksum-algorithm", "x-amz-content-sha256"].includes(key) || headers.has(key)) throw new HierarchyAccessError();
    headers.set(name, value);
  }
  if (headers.get("content-type") !== pending.contentType) throw new HierarchyAccessError();
  await authorize(bounded);
  bounded.throwIfAborted();
  // A signed storage request receives no session, credentials or referrer, and
  // cannot redirect file contents elsewhere. The browser supplies Content-Length.
  const uploaded = await fetch(url, { method: "PUT", body: bytes, headers, credentials: "omit", redirect: "error", referrerPolicy: "no-referrer", cache: "no-store", signal: bounded });
  await uploaded.body?.cancel();
  if (!uploaded.ok) throw new Error("The file transfer couldn't be confirmed. Resume with the original file.");
  await authorize(bounded);
  const completed = await finishUpload(selection, pending, bounded);
  if (!completed) throw new Error("The saved file couldn't be confirmed. Check the upload before starting again.");
  await authorize(bounded);
  bounded.throwIfAborted();
  return completed;
}
